import type {NormalizedCacheObject} from '@apollo/client';
import {
    type ApolloClientProxy,
    type BaseHttpCodes,
    EntityBaseGqlService,
    type GetDownloadLinkRequestPayload,
    type ServiceResponsePayload,
} from '@igp/shared/api';
import type {Mutation, PayoutReport, Query, QueryGetPayoutReportsArgs, Subscription} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import type {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {PayoutReportRequestBuilder} from './requestBuilder';
import type {PayoutReportDownloadFields, PayoutReportFilterKeys, PayoutReportQueryFields} from './types';

@injectable()
export class PayoutReportService extends EntityBaseGqlService<
    EntityType,
    Query,
    QueryGetPayoutReportsArgs,
    PayoutReportQueryFields,
    Mutation,
    Subscription,
    BaseHttpCodes,
    PayoutReportFilterKeys
> {
    private readonly _requestBuilder: PayoutReportRequestBuilder;

    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer
    ) {
        const requestBuilder = new PayoutReportRequestBuilder();
        super(client, requestBuilder, messageLocalizer);
        this._requestBuilder = requestBuilder;
    }

    get(
        requestPayload: EntityFetchRequestPayload<EntityType, PayoutReportQueryFields>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, PayoutReport>> {
        return this.fetchData(requestPayload).pipe(
            map(r => {
                return {
                    ...r,
                    requestPayload,
                    responsePayload: {
                        items: r.responsePayload?.getPayoutReports?.items ?? [],
                        total: r.responsePayload?.getPayoutReports?.total_count ?? 0,
                    },
                };
            })
        );
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<PayoutReportDownloadFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<PayoutReportDownloadFields>, string>> {
        const request = this._requestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: response?.responsePayload?.exportPayoutReports?.url,
            }))
        );
    }

    protected getErrorSeverity(_code: BaseHttpCodes): number {
        return 0;
    }
}
